import { createApiRef, discoveryApiRef, fetchApiRef, OAuthApi } from '@backstage/core-plugin-api';
import { Octokit } from '@octokit/rest';
import yaml from 'js-yaml';

// ── Constants ─────────────────────────────────────────────────────────────────

/** GitHub organisation used for all repository operations */
const GITHUB_ORG = 'AAInternal';

/** Backend API endpoint for AI resources deletion (appended to github backend URL) */
const BACKEND_ENDPOINT = '/ai-resources/delete-apim-secrets';

/** Backend API endpoint for HiddenLayer policy control decomposition workflow */
const HIDDEN_LAYER_BACKEND_ENDPOINT = '/ai-resources/delete-hidden-layer-policy-control';

// ── Types ─────────────────────────────────────────────────────────────────────

/** Parameters required to delete Azure APIM secrets */
export interface DeleteAzureAPIMSecretsParams {
  /** APIM environment (nonprod or prod) */
  apiManagementEnvironment: 'nonprod' | 'prod';
  /** Product ID to delete */
  productId: string;
  /** Vault namespace */
  vaultNamespace: string;
  /** Employee ID for audit purposes */
  employeeID: string;
}

/** Parameters required to trigger HiddenLayer policy control decomposition */
export interface DeleteHiddenLayerPolicyControlParams {
  /** Project name in HiddenLayer workflow */
  project_name: string;
  /** Application shortname in HiddenLayer workflow */
  shortname: string;
}

// ── Service interface & API ref ───────────────────────────────────────────────

/**
 * Client-side service for AI resource deletion operations.
 */
export interface AIResourcesDeletionServiceApi {
  /**
   * Fetches the VAULT_NAMESPACE value from the entity's generate-key.yaml workflow,
   * stripping the `automation/` prefix.
   *
   * @param repoName - The repository name to fetch from
   * @returns The vault namespace value without the `automation/` prefix
   * @throws Error if the file cannot be fetched or the value is not found
   */
  fetchVaultNamespace(repoName: string): Promise<string>;

  /**
   * Triggers the run-delete workflow dispatch to remove Azure APIM secrets.
   *
   * @param params - The deletion parameters
   * @throws Error if the workflow dispatch fails
   */
  deleteAzureAPIMSecrets(params: DeleteAzureAPIMSecretsParams): Promise<void>;

  /**
   * Triggers HiddenLayer policy control decomposition workflow.
   *
   * @param params - The workflow input parameters
   * @throws Error if the workflow dispatch fails
   */
  deleteHiddenLayerPolicyControl(params: DeleteHiddenLayerPolicyControlParams): Promise<void>;
}

export const AIResourcesDeletionServiceApiRef = createApiRef<AIResourcesDeletionServiceApi>({
  id: 'plugin.devkit.ai-resources-deletion.service',
});

// ── Service implementation ────────────────────────────────────────────────────

/**
 * Uses user OAuth for read-only repo metadata and backend endpoint for
 * workflow dispatch to ensure GitHub App credentials are used for privileged actions.
 */
export class AIResourcesDeletionService implements AIResourcesDeletionServiceApi {
  constructor(
    private readonly ghecApiUrl: string,
    private readonly ghecAuthApi: OAuthApi,
    private readonly discoveryApi: typeof discoveryApiRef.T,
    private readonly fetchApi: typeof fetchApiRef.T,
  ) {}

  /** Creates an authenticated Octokit REST client. */
  private async getOctokit(): Promise<Octokit> {
    const token = await this.ghecAuthApi.getAccessToken();
    return new Octokit({ auth: token, baseUrl: this.ghecApiUrl });
  }

  async fetchVaultNamespace(repoName: string): Promise<string> {
    const octokit = await this.getOctokit();

    const response = await octokit.repos.getContent({
      owner: GITHUB_ORG,
      repo: repoName,
      path: '.github/workflows/generate-key.yaml',
    });

    if (!('content' in response.data) || typeof response.data.content !== 'string') {
      throw new Error(`Unable to read generate-key.yaml from ${repoName}`);
    }

    const fileContent = Buffer.from(response.data.content, 'base64').toString('utf-8');

    const workflow = yaml.load(fileContent) as Record<string, unknown>;
    const jobs = workflow?.jobs as Record<string, Record<string, unknown>> | undefined;
    const job = jobs?.['generate-subscription-key'];
    const withBlock = job?.with as Record<string, string> | undefined;
    const rawValue = withBlock?.VAULT_NAMESPACE;

    if (!rawValue) {
      throw new Error(`VAULT_NAMESPACE not found in ${repoName}/.github/workflows/generate-key.yaml`);
    }

    return rawValue.replace(/^automation\//, '');
  }

  async deleteAzureAPIMSecrets(params: DeleteAzureAPIMSecretsParams): Promise<void> {
    try {
      const backendUrl = await this.discoveryApi.getBaseUrl('github');

      const response = await this.fetchApi.fetch(`${backendUrl}${BACKEND_ENDPOINT}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(params),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.message || errorData.error || 'Unknown error';
        throw new Error(`Backend request failed: ${errorMessage}`);
      }
    } catch (error) {
      throw new Error(
        `Failed to trigger Azure APIM secrets deletion workflow: ${
          error instanceof Error ? error.message : 'An unknown error occurred'
        }`,
      );
    }
  }

  async deleteHiddenLayerPolicyControl(params: DeleteHiddenLayerPolicyControlParams): Promise<void> {
    try {
      const backendUrl = await this.discoveryApi.getBaseUrl('github');

      const response = await this.fetchApi.fetch(`${backendUrl}${HIDDEN_LAYER_BACKEND_ENDPOINT}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(params),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.message || errorData.error || 'Unknown error';
        throw new Error(`Backend request failed: ${errorMessage}`);
      }
    } catch (error) {
      throw new Error(
        `Failed to trigger HiddenLayer policy control deletion workflow: ${
          error instanceof Error ? error.message : 'An unknown error occurred'
        }`,
      );
    }
  }
}
